<template>
  <section class="manager-section">
    <div v-if="view === 'list'">
      <header class="manager-page-header">
        <div>
          <h1>{{ title }}</h1>
          <p>{{ subtitle }}</p>
        </div>
        <button class="manager-button manager-button-primary" type="button" @click="startCreate">
          Add {{ singular.toLowerCase() }}
        </button>
      </header>

      <div class="manager-toolbar">
        <input
          v-model.trim="search"
          class="manager-toolbar-search"
          type="search"
          :placeholder="searchPlaceholder"
          :aria-label="'Search ' + title.toLowerCase()"
        >
      </div>

      <div v-if="message" class="manager-notice manager-notice-success">{{ message }}</div>
      <div v-if="error" class="manager-notice manager-notice-error">{{ error }}</div>

      <div class="manager-table-wrap">
        <table class="manager-table">
          <thead>
            <tr>
              <th>{{ resource === 'agent' ? 'Name' : 'Development' }}</th>
              <th>{{ resource === 'agent' ? 'Email' : 'Location' }}</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filteredRows" :key="row.privateRefId">
              <td><strong>{{ row.name || 'Untitled' }}</strong><span>{{ row.meta }}</span></td>
              <td>{{ row.secondary || '—' }}</td>
              <td><span class="manager-status">{{ row.status }}</span></td>
              <td class="manager-table-action">
                <button class="manager-button manager-button-secondary" type="button" @click="startEdit(row)">Edit</button>
              </td>
            </tr>
            <tr v-if="!loading && !filteredRows.length"><td colspan="4" class="manager-empty">No records found.</td></tr>
            <tr v-if="loading"><td colspan="4" class="manager-empty">Loading…</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else>
      <header class="manager-page-header">
        <div>
          <button class="manager-back" type="button" @click="returnToList">← {{ title }}</button>
          <h1>{{ privateRefId ? 'Edit ' + singular.toLowerCase() : 'Add ' + singular.toLowerCase() }}</h1>
        </div>
        <div class="manager-header-actions">
          <button v-if="privateRefId" class="manager-button manager-button-danger" type="button" @click="removeRecord" :disabled="saving || uploadingImage">Delete</button>
          <button class="manager-button manager-button-primary" type="button" @click="saveRecord" :disabled="saving || uploadingImage">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
      </header>

      <div v-if="message" class="manager-notice manager-notice-success">{{ message }}</div>
      <div v-if="error" class="manager-notice manager-notice-error">{{ error }}</div>

      <form class="manager-form" @submit.prevent="saveRecord">
        <section v-if="resource === 'agent'" class="manager-form-section">
          <h2>Agent details</h2>
          <div class="manager-form-grid">
            <label><span>First name</span><input v-model.trim="form.firstName" required></label>
            <label><span>Last name</span><input v-model.trim="form.lastName" required></label>
            <label><span>Job title</span><input v-model.trim="form.jobTitle"></label>
            <label><span>Status</span><select v-model="form.status"><option>Active</option><option>Inactive</option></select></label>
            <label class="manager-checkbox">
              <input v-model="form.featured" type="checkbox">
              <span>Featured agent</span>
            </label>
            <label><span>Email</span><input v-model.trim="form.email" type="email" required></label>
            <label><span>Cell number</span><input v-model.trim="form.cell"></label>
            <label><span>Office number</span><input v-model.trim="form.officeNumber"></label>
            <label><span>Office</span><input v-model.trim="form.office"></label>
            <div class="manager-field-wide manager-agent-photo-manager">
              <div class="manager-image-heading">
                <div>
                  <strong>Agent photo</strong>
                  <small>The image is resized and compressed when uploaded.</small>
                </div>
              </div>

              <div v-if="form.photo" class="manager-agent-photo-preview">
                <img :src="form.photo" :alt="form.firstName + ' ' + form.lastName">
                <button type="button" class="manager-button manager-button-danger" @click="removeAgentPhoto" :disabled="uploadingImage">Remove photo</button>
              </div>

              <label
                class="manager-upload-dropzone manager-upload-dropzone-small"
                :class="{ disabled: uploadingImage || !internalId, locked: !internalId }"
              >
                <input
                  ref="agentPhotoInput"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  :disabled="uploadingImage || !internalId"
                  @change="uploadAgentPhoto"
                >
                <strong>{{ !internalId ? 'Save agent before uploading a photo' : (uploadingImage ? 'Uploading and compressing…' : (form.photo ? 'Replace agent photo' : 'Choose agent photo')) }}</strong>
                <span>{{ !internalId ? 'The photo uploader becomes available after the agent has a database ID.' : 'JPEG, PNG or WebP.' }}</span>
              </label>

              <div v-if="uploadingImage" class="manager-upload-progress">
                <span :style="{ width: imageProgress + '%' }"></span>
              </div>
            </div>
            <label class="manager-field-wide"><span>Biography</span><textarea v-model="form.bio" rows="6"></textarea></label>
          </div>
        </section>

        <section v-else class="manager-form-section">
          <h2>Development details</h2>
          <div class="manager-form-grid">
            <label class="manager-field-wide"><span>Name</span><input v-model.trim="form.name" required></label>
            <label><span>Location</span><input v-model.trim="form.location"></label>
            <label><span>Status</span><select v-model="form.status"><option>Active</option><option>Draft</option><option>Archived</option></select></label>
            <label><span>Price from</span><input v-model.number="form.priceFrom" type="number" min="0"></label>
            <label><span>Website URL</span><input v-model.trim="form.website"></label>
            <label class="manager-field-wide"><span>Description</span><textarea v-model="form.description" rows="8"></textarea></label>
            <div class="manager-field-wide manager-development-pack-manager">
              <div class="manager-image-heading">
                <div>
                  <strong>Development download pack</strong>
                  <small>Upload the PDF brochure or ZIP pack visitors receive after completing the enquiry form.</small>
                </div>
                <span>{{ form.downloadPackUrl ? 'Uploaded' : 'Not uploaded' }}</span>
              </div>

              <div v-if="form.downloadPackUrl" class="manager-file-preview">
                <div>
                  <strong>{{ form.downloadPackName || 'Development pack' }}</strong>
                  <a :href="form.downloadPackUrl" target="_blank" rel="noopener noreferrer">Open current file</a>
                </div>
                <button
                  type="button"
                  class="manager-button manager-button-danger"
                  :disabled="uploadingImage"
                  @click="removeDevelopmentPack"
                >
                  Remove pack
                </button>
              </div>

              <label
                class="manager-upload-dropzone manager-upload-dropzone-small"
                :class="{ disabled: uploadingImage || !internalId, locked: !internalId }"
              >
                <input
                  ref="developmentPackInput"
                  type="file"
                  accept=".pdf,.zip,application/pdf,application/zip,application/x-zip-compressed"
                  :disabled="uploadingImage || !internalId"
                  @change="uploadDevelopmentPack"
                >
                <strong>{{ developmentPackUploadLabel }}</strong>
                <span>{{ !internalId ? 'Save once to create the database ID and unlock this uploader.' : 'PDF or ZIP, up to 50 MB.' }}</span>
              </label>

              <div v-if="uploadingImage" class="manager-upload-progress">
                <span :style="{ width: imageProgress + '%' }"></span>
              </div>
            </div>

            <div class="manager-field-wide manager-development-image-manager">
              <div class="manager-image-heading">
                <div>
                  <strong>Development images</strong>
                  <small>Add up to four images. They are resized and compressed when uploaded.</small>
                </div>
                <span>{{ form.images.length }} / 4</span>
              </div>

              <div v-if="form.images.length" class="manager-development-gallery">
                <div v-for="(image, imageIndex) in form.images" :key="image" class="manager-development-gallery-item">
                  <img :src="image" :alt="form.name + ' image ' + (imageIndex + 1)">
                  <button
                    type="button"
                    class="manager-button manager-button-danger"
                    :disabled="uploadingImage"
                    @click="removeDevelopmentGalleryImage(image, imageIndex)"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <label
                class="manager-upload-dropzone manager-upload-dropzone-small"
                :class="{ disabled: uploadingImage || !internalId || form.images.length >= 4, locked: !internalId }"
              >
                <input
                  ref="developmentImagesInput"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  :disabled="uploadingImage || !internalId || form.images.length >= 4"
                  @change="uploadDevelopmentGalleryImages"
                >
                <strong>{{ developmentImageUploadLabel }}</strong>
                <span>{{ !internalId ? 'The gallery becomes available after the development has a database ID.' : 'JPEG, PNG or WebP. Select up to ' + (4 - form.images.length) + ' more.' }}</span>
              </label>

              <div v-if="uploadingImage" class="manager-upload-progress">
                <span :style="{ width: imageProgress + '%' }"></span>
              </div>
            </div>
          </div>
        </section>

        <div class="manager-form-footer">
          <button class="manager-button manager-button-secondary" type="button" @click="returnToList">Cancel</button>
          <button class="manager-button manager-button-primary" type="submit" :disabled="saving || uploadingImage">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import {
  API_BASE,
  API_KEY,
  apiErrorMessage,
  createManagedRecord,
  deleteAgentImage,
  deleteManagedRecord,
  getAgentData,
  getDevelopmentData,
  getRecordPayload,
  listManagedRecords,
  normaliseInternalId,
  uploadAgentImage,
  updateManagedRecord
} from '@/services/winelandsApi'

function blankAgent() {
  return {
    firstName: '', lastName: '', jobTitle: '', email: '', cell: '', officeNumber: '',
    office: '', photo: '', bio: '', status: 'Active', featured: false
  }
}

function featuredValue(value) {
  return value === true || value === 1 || /^(1|true|yes)$/i.test(String(value || ''))
}

function isFeaturedAgent(record, agent) {
  const payload = getRecordPayload(record)
  return featuredValue(payload.is_featured_agent) ||
    featuredValue(agent.is_featured_agent) ||
    featuredValue(agent.featured)
}

function blankDevelopment() {
  return {
    name: '', location: '', status: 'Active', priceFrom: 0, website: '', description: '',
    images: [], downloadPackUrl: '', downloadPackName: ''
  }
}

function normaliseMediaUrl(value) {
  if (typeof value === 'string') return value.trim()
  if (!value || typeof value !== 'object') return ''
  return String(value.url || value.src || value.path || '').trim()
}

function normaliseDevelopmentImages(payload, item) {
  const candidates = []
  const sources = [
    item.images,
    item.development_images,
    payload.development_images
  ]

  sources.forEach(function (source) {
    if (Array.isArray(source)) {
      source.forEach(function (image) {
        const url = normaliseMediaUrl(image)
        if (url && candidates.indexOf(url) === -1) candidates.push(url)
      })
    }
  })

  const legacyFeatured = normaliseMediaUrl(item.featured_image || payload.featured_image)
  if (legacyFeatured && candidates.indexOf(legacyFeatured) === -1) candidates.unshift(legacyFeatured)
  return candidates.slice(0, 4)
}

function normaliseDevelopmentPack(payload, item) {
  const pack = item.download_pack || payload.download_pack || {}
  const url = normaliseMediaUrl(pack) ||
    normaliseMediaUrl(item.download_pack_url) ||
    normaliseMediaUrl(payload.download_pack_url)
  const name = String(
    (pack && typeof pack === 'object' && (pack.name || pack.file_name)) ||
    item.download_pack_name ||
    payload.download_pack_name ||
    ''
  ).trim()

  return { url: url, name: name }
}

export default {
  name: 'SimpleManager',

  props: {
    resource: { type: String, required: true }
  },

  data: function () {
    return {
      records: [],
      view: 'list',
      privateRefId: '',
      internalId: null,
      form: this.resource === 'agent' ? blankAgent() : blankDevelopment(),
      search: '',
      loading: false,
      saving: false,
      uploadingImage: false,
      imageProgress: 0,
      error: '',
      message: '',
      savedFormState: ''
    }
  },

  computed: {
    title: function () { return this.resource === 'agent' ? 'Agents' : 'Developments' },
    singular: function () { return this.resource === 'agent' ? 'Agent' : 'Development' },
    subtitle: function () { return this.resource === 'agent' ? 'Winelands agent profiles' : 'Winelands developments' },
    searchPlaceholder: function () {
      return this.resource === 'agent'
        ? 'Search by agent name, email or status'
        : 'Search by development, location or status'
    },
    isDirty: function () {
      return this.view === 'editor' && this.savedFormState !== this.formState()
    },
    developmentImageUploadLabel: function () {
      if (!this.internalId) return 'Save development before uploading images'
      if (this.uploadingImage) return 'Uploading and compressing…'
      if (this.form.images.length >= 4) return 'Maximum of four images reached'
      return this.form.images.length ? 'Add more images' : 'Choose development images'
    },
    developmentPackUploadLabel: function () {
      if (!this.internalId) return 'Save development before uploading a pack'
      if (this.uploadingImage) return 'Uploading pack…'
      return this.form.downloadPackUrl ? 'Replace development pack' : 'Choose development pack'
    },

    rows: function () {
      return this.records.map(function (record) {
        const item = this.resource === 'agent' ? getAgentData(record) : getDevelopmentData(record)
        return {
          record: record,
          privateRefId: record.ref_id,
          internalId: normaliseInternalId(record.id),
          name: this.resource === 'agent'
            ? [item.first_name, item.last_name].filter(Boolean).join(' ')
            : (item.name || item.title),
          meta: this.resource === 'agent'
            ? [item.job_title, isFeaturedAgent(record, item) ? 'Featured' : ''].filter(Boolean).join(' · ')
            : (normaliseDevelopmentPack(getRecordPayload(record), item).url ? 'Download pack uploaded' : 'No download pack'),
          secondary: this.resource === 'agent' ? item.email_address : (item.location || item.loc_suburb),
          status: item.status || 'Active'
        }
      }, this)
    },

    filteredRows: function () {
      const search = this.search.toLowerCase()
      return this.rows.filter(function (row) {
        return !search || [row.name, row.meta, row.secondary, row.status].join(' ').toLowerCase().indexOf(search) !== -1
      })
    }
  },

  watch: {
    resource: function () {
      this.view = 'list'
      this.privateRefId = ''
      this.internalId = null
      this.form = this.resource === 'agent' ? blankAgent() : blankDevelopment()
      this.markFormClean()
      this.load()
    }
  },

  mounted: function () { this.load() },

  methods: {
    load: async function () {
      this.loading = true
      this.error = ''
      try {
        this.records = await listManagedRecords(this.resource)
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not load records.')
      } finally {
        this.loading = false
      }
    },

    startCreate: function () {
      this.privateRefId = ''
      this.internalId = null
      this.form = this.resource === 'agent' ? blankAgent() : blankDevelopment()
      this.error = ''
      this.message = ''
      this.uploadingImage = false
      this.imageProgress = 0
      this.view = 'editor'
      this.markFormClean()
      window.scrollTo(0, 0)
    },

    startEdit: function (row) {
      const item = this.resource === 'agent' ? getAgentData(row.record) : getDevelopmentData(row.record)
      this.privateRefId = row.privateRefId
      this.internalId = row.internalId
      if (this.resource === 'agent') {
        this.form = {
          firstName: item.first_name || '', lastName: item.last_name || '', jobTitle: item.job_title || '',
          email: item.email_address || '', cell: item.cell_number || '', officeNumber: item.office_number || '',
          office: item.office || '', photo: item.agent_photo || '', bio: item.bio || '', status: item.status || 'Active',
          featured: isFeaturedAgent(row.record, item)
        }
      } else {
        const payload = getRecordPayload(row.record)
        const images = normaliseDevelopmentImages(payload, item)
        const pack = normaliseDevelopmentPack(payload, item)
        this.form = {
          name: item.name || item.title || '', location: item.location || item.loc_suburb || '', status: item.status || 'Active',
          priceFrom: Number(item.price_from || 0), website: item.website_url || '', description: item.description || '',
          images: images, downloadPackUrl: pack.url, downloadPackName: pack.name
        }
      }
      this.error = ''
      this.message = ''
      if (!this.internalId) {
        this.error = 'This record does not have a valid database ID yet. Save it after correcting AUTO_INCREMENT before uploading images.'
      }
      this.uploadingImage = false
      this.imageProgress = 0
      this.view = 'editor'
      this.markFormClean()
      window.scrollTo(0, 0)
    },

    returnToList: function () {
      if (this.hasUnsavedChanges()) {
        this.$emit('request-navigation')
        return
      }
      this.forceReturnToList()
    },

    forceReturnToList: function () {
      this.view = 'list'
      this.error = ''
      window.scrollTo(0, 0)
    },

    formState: function () {
      return JSON.stringify(this.form)
    },

    markFormClean: function () {
      this.savedFormState = this.formState()
    },

    hasUnsavedChanges: function () {
      return this.isDirty
    },

    isEditingView: function () {
      return this.view === 'editor'
    },

    isBusy: function () {
      return this.saving || this.uploadingImage
    },

    saveForNavigation: async function () {
      return this.saveRecord()
    },

    uploadAgentPhoto: async function (event) {
      const file = event.target.files && event.target.files[0]
      this.error = ''

      if (!file) return
      if (!this.internalId) {
        this.error = 'Save the agent before uploading a photo.'
        event.target.value = ''
        return
      }

      this.uploadingImage = true
      this.imageProgress = 0
      try {
        const url = await uploadAgentImage(this.internalId, file, this.form.photo, function (progress) {
          this.imageProgress = progress
        }.bind(this))
        if (url) this.form.photo = url
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not reach upload_agent_image.php. Confirm it is deployed in the /api/ directory.')
      } finally {
        this.uploadingImage = false
        this.imageProgress = 0
        event.target.value = ''
      }
    },

    removeAgentPhoto: async function () {
      if (!this.form.photo || !window.confirm('Remove this agent photo?')) return

      this.error = ''
      this.uploadingImage = true
      try {
        if (this.internalId && this.form.photo.indexOf('/uploads/agents/') !== -1) {
          await deleteAgentImage(this.internalId, this.form.photo)
        }
        this.form.photo = ''
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not remove the agent photo.')
      } finally {
        this.uploadingImage = false
      }
    },

    developmentMediaRequest: async function (formData, onProgress) {
      const response = await axios.post(API_BASE + '/upload_development_media.php', formData, {
        headers: {
          Accept: 'application/json',
          'X-API-Key': API_KEY
        },
        timeout: 120000,
        onUploadProgress: function (event) {
          if (!onProgress || !event.total) return
          onProgress(Math.round((event.loaded * 100) / event.total))
        }
      })

      return response.data || {}
    },

    deleteDevelopmentMedia: async function (action, url) {
      const response = await axios.post(API_BASE + '/upload_development_media.php', {
        action: action,
        id: this.internalId,
        url: url
      }, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        }
      })

      return response.data || {}
    },

    uploadDevelopmentGalleryImages: async function (event) {
      const selected = Array.prototype.slice.call(event.target.files || [])
      this.error = ''

      if (!selected.length) return
      if (!this.internalId) {
        this.error = 'Save the development before uploading images.'
        event.target.value = ''
        return
      }
      const remaining = 4 - this.form.images.length
      if (selected.length > remaining) {
        this.error = 'A development can have a maximum of four images. Select no more than ' + remaining + '.'
        event.target.value = ''
        return
      }

      this.uploadingImage = true
      this.imageProgress = 0
      try {
        const formData = new FormData()
        formData.append('action', 'upload_images')
        formData.append('id', String(this.internalId))
        formData.append('existing_count', String(this.form.images.length))
        selected.forEach(function (file) {
          formData.append('images[]', file)
        })

        const body = await this.developmentMediaRequest(formData, function (progress) {
          this.imageProgress = progress
        }.bind(this))
        const uploaded = Array.isArray(body.urls) ? body.urls.filter(Boolean) : []
        this.form.images = this.form.images.concat(uploaded).filter(function (url, index, list) {
          return list.indexOf(url) === index
        }).slice(0, 4)
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not upload the development images.')
      } finally {
        this.uploadingImage = false
        this.imageProgress = 0
        event.target.value = ''
      }
    },

    removeDevelopmentGalleryImage: async function (url, imageIndex) {
      if (!url || !window.confirm('Remove this development image?')) return

      this.error = ''
      this.uploadingImage = true
      try {
        if (this.internalId && url.indexOf('/uploads/developments/') !== -1) {
          await this.deleteDevelopmentMedia('delete_image', url)
        }
        this.form.images.splice(imageIndex, 1)
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not remove the development image.')
      } finally {
        this.uploadingImage = false
      }
    },

    uploadDevelopmentPack: async function (event) {
      const file = event.target.files && event.target.files[0]
      this.error = ''

      if (!file) return
      if (!this.internalId) {
        this.error = 'Save the development before uploading a pack.'
        event.target.value = ''
        return
      }

      this.uploadingImage = true
      this.imageProgress = 0
      try {
        const formData = new FormData()
        formData.append('action', 'upload_pack')
        formData.append('id', String(this.internalId))
        formData.append('pack', file)
        if (this.form.downloadPackUrl) formData.append('previous_url', this.form.downloadPackUrl)

        const body = await this.developmentMediaRequest(formData, function (progress) {
          this.imageProgress = progress
        }.bind(this))
        this.form.downloadPackUrl = body.url || ''
        this.form.downloadPackName = body.original_name || body.file_name || file.name
        this.message = 'Development pack uploaded. Save the development to publish this file.'
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not upload the development pack.')
      } finally {
        this.uploadingImage = false
        this.imageProgress = 0
        event.target.value = ''
      }
    },

    removeDevelopmentPack: async function () {
      if (!this.form.downloadPackUrl || !window.confirm('Remove this development pack?')) return

      this.error = ''
      this.uploadingImage = true
      try {
        if (this.internalId && this.form.downloadPackUrl.indexOf('/uploads/developments/') !== -1) {
          await this.deleteDevelopmentMedia('delete_pack', this.form.downloadPackUrl)
        }
        this.form.downloadPackUrl = ''
        this.form.downloadPackName = ''
        this.message = 'Development pack removed. Save the development to update the public record.'
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not remove the development pack.')
      } finally {
        this.uploadingImage = false
      }
    },

    payload: function () {
      if (this.resource === 'agent') {
        const oldRecord = this.privateRefId
          ? this.records.find(function (record) { return record.ref_id === this.privateRefId }, this)
          : null
        const oldPayload = oldRecord ? getRecordPayload(oldRecord) : {}
        const oldData = oldPayload.data || {}
        const oldAgent = oldRecord ? getAgentData(oldRecord) : {}

        return Object.assign({}, oldPayload, {
          schema_version: 1,
          is_featured_agent: this.form.featured,
          data: Object.assign({}, oldData, { agent: [Object.assign({}, oldAgent, {
            first_name: this.form.firstName,
            last_name: this.form.lastName,
            job_title: this.form.jobTitle,
            email_address: this.form.email,
            cell_number: this.form.cell,
            office_number: this.form.officeNumber,
            agent_photo: this.form.photo,
            bio: this.form.bio,
            office: this.form.office,
            status: this.form.status,
            profile_url: oldAgent.profile_url || '',
            facebook_url: oldAgent.facebook_url || '',
            instagram_url: oldAgent.instagram_url || '',
            linkedin_url: oldAgent.linkedin_url || ''
          })] })
        })
      }

      const oldPayload = this.privateRefId
        ? getRecordPayload(this.records.find(function (record) { return record.ref_id === this.privateRefId }, this))
        : {}
      const oldData = oldPayload.data || {}
      const oldDevelopment = this.privateRefId
        ? getDevelopmentData(this.records.find(function (record) { return record.ref_id === this.privateRefId }, this))
        : {}
      const images = this.form.images.slice(0, 4)
      const downloadPack = this.form.downloadPackUrl
        ? { url: this.form.downloadPackUrl, name: this.form.downloadPackName }
        : null

      return Object.assign({}, oldPayload, {
        schema_version: 1,
        featured_image: images[0] || '',
        development_images: images,
        download_pack: downloadPack,
        data: Object.assign({}, oldData, { development: [Object.assign({}, oldDevelopment, {
          name: this.form.name,
          location: this.form.location,
          status: this.form.status,
          price_from: Number(this.form.priceFrom || 0),
          website_url: this.form.website,
          featured_image: images[0] || '',
          images: images,
          description: this.form.description,
          download_pack: downloadPack
        })] })
      })
    },

    saveRecord: async function () {
      if (this.uploadingImage) {
        this.error = 'Please wait for the image upload to finish.'
        return false
      }
      this.saving = true
      this.error = ''
      this.message = ''
      try {
        const creating = !this.privateRefId
        let createResponse = null

        if (this.privateRefId) {
          await updateManagedRecord(this.resource, this.privateRefId, this.payload())
        } else {
          createResponse = await createManagedRecord(this.resource, this.payload())
        }

        await this.load()
        this.markFormClean()

        if (creating) {
          const createdRecordData = createResponse && createResponse.record
            ? createResponse.record
            : (createResponse && createResponse.data && !Array.isArray(createResponse.data)
              ? createResponse.data
              : {})
          const createdRefId = String(
            (createResponse && createResponse.ref_id) ||
            createdRecordData.ref_id ||
            ''
          ).trim()
          const createdId = normaliseInternalId(
            (createResponse && createResponse.id) ||
            createdRecordData.id
          )
          const createdRecord = this.records.find(function (record) {
            if (createdRefId && record.ref_id === createdRefId) return true
            return Boolean(createdId && normaliseInternalId(record.id) === createdId)
          })

          if (createdRecord) {
            this.privateRefId = createdRecord.ref_id
            this.internalId = normaliseInternalId(createdRecord.id)
          } else {
            this.privateRefId = createdRefId
            this.internalId = createdId
          }

          if (this.privateRefId && this.internalId) {
            this.message = this.resource === 'development'
              ? 'Development saved. Images and the downloadable-pack uploader are now available below.'
              : 'Agent saved. The photo uploader is now available below.'
            this.view = 'editor'
          } else {
            this.message = this.singular + ' saved. Open it again to add media.'
            this.view = 'list'
          }
        } else {
          this.message = this.singular + ' saved.'
          this.view = 'list'
        }
        return true
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not save the record.')
        return false
      } finally {
        this.saving = false
      }
    },

    removeRecord: async function () {
      if (!this.privateRefId || !window.confirm('Delete this ' + this.singular.toLowerCase() + '?')) return
      this.saving = true
      this.error = ''
      try {
        await deleteManagedRecord(this.resource, this.privateRefId)
        await this.load()
        this.markFormClean()
        this.message = this.singular + ' deleted.'
        this.view = 'list'
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not delete the record.')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.manager-development-gallery {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin: 1rem 0;
}

.manager-development-gallery-item {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  min-width: 0;
}

.manager-development-gallery-item img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
}

.manager-file-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem 0;
  padding: 1rem;
  border: 1px solid #d9e1ec;
  border-radius: 8px;
  background: #f7f9fc;
}

.manager-file-preview > div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.manager-file-preview a {
  color: #255699;
  word-break: break-word;
}

@media (max-width: 900px) {
  .manager-development-gallery {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .manager-development-gallery {
    grid-template-columns: 1fr;
  }

  .manager-file-preview {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
